/**
 * bid_limit 编辑器：targeting_id=JSON 数组
 * `[{"rta_id","external_action","bidlimit"}, ...]`，ext_info=描述串（只读）。
 * 行式录入：RTA ID + 出价目标 + 出价上限（数字）+ 删除；
 * 「添加」需填 RTA ID + 出价目标 + 上限。序列化回 JSON 数组。
 */
import { useState } from "react";
import type { EditorProps } from "../redlineConfig/types";
import { parseJsonExt, stringifyJsonExt } from "../../utils/redlineHelpers";

interface BidLimitRow {
  rta_id: string;
  external_action: string;
  bidlimit: number;
}

export function OceanBidLimitEditor({ item, onChange }: EditorProps) {
  const rows = parseJsonExt<BidLimitRow[]>(item.targeting_id, []);

  const [rtaDraft, setRtaDraft] = useState("");
  const [actionDraft, setActionDraft] = useState("");
  const [bidDraft, setBidDraft] = useState("");

  function commit(next: BidLimitRow[]) {
    onChange({
      targeting_id: stringifyJsonExt(next.filter((r) => r.rta_id && r.external_action)),
      ext_info: item.ext_info ?? "",
    });
  }

  function setField(i: number, patch: Partial<BidLimitRow>) {
    commit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }

  function remove(i: number) {
    commit(rows.filter((_, j) => j !== i));
  }

  function add() {
    const rta = rtaDraft.trim();
    const action = actionDraft.trim();
    const bid = Number(bidDraft);
    if (!rta || !action || !Number.isFinite(bid)) return;
    commit([...rows, { rta_id: rta, external_action: action, bidlimit: bid }]);
    setRtaDraft("");
    setActionDraft("");
    setBidDraft("");
  }

  return (
    <div className="space-y-2">
      <div className="text-[11px] text-text-muted">
        类别（ext_info）：<span className="leo-tag ml-1">{item.ext_info || "—"}</span>
        （描述，不可编辑）
      </div>
      {rows.map((r, i) => (
        <div key={`${r.rta_id}-${r.external_action}-${i}`} className="rl-subbox flex flex-wrap items-center gap-2">
          <input
            type="text"
            className="leo-input min-w-[8rem] flex-[2]"
            value={r.rta_id}
            onChange={(e) => setField(i, { rta_id: e.target.value })}
            placeholder="RTA ID（如 19138）"
          />
          <input
            type="text"
            className="leo-input min-w-[16rem] flex-[3]"
            value={r.external_action}
            onChange={(e) => setField(i, { external_action: e.target.value })}
            placeholder="出价目标（如 AD_CONVERT_TYPE_LOAN_COMPLETION）"
          />
          <input
            type="number"
            className="leo-input w-[8rem] flex-[1]"
            value={r.bidlimit}
            onChange={(e) => setField(i, { bidlimit: Number(e.target.value) })}
            placeholder="出价上限"
          />
          <button
            type="button"
            className="leo-btn-ghost shrink-0"
            onClick={() => remove(i)}
            aria-label="移除该出价规则"
          >
            删除
          </button>
        </div>
      ))}
      <div className="flex flex-nowrap items-center gap-2 border-t border-line-subtle pt-2">
        <input
          type="text"
          className="leo-input min-w-0 flex-[2]"
          placeholder="新 RTA ID"
          value={rtaDraft}
          onChange={(e) => setRtaDraft(e.target.value)}
        />
        <input
          type="text"
          className="leo-input min-w-0 flex-[3]"
          placeholder="新出价目标"
          value={actionDraft}
          onChange={(e) => setActionDraft(e.target.value)}
        />
        <input
          type="number"
          className="leo-input w-[8rem] flex-[1]"
          placeholder="新上限"
          value={bidDraft}
          onChange={(e) => setBidDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="leo-btn-ghost shrink-0" onClick={add}>
          添加
        </button>
      </div>
    </div>
  );
}
